import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';

interface CallSession {
  id: string;
  aircall_call_id: number;
  direction: string;
  status: string;
  from_number: string | null;
  to_number: string | null;
  agent_name: string | null;
  started_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
  lead_id: string | null;
}

interface LinkedFuneral {
  funeral_id: number;
  service_id: number;
  deceased_name: string | null;
  cremation_status: string | null;
}

interface LinkedLead {
  lead_id: string;
  contact_name: string | null;
  intent: string | null;
  stage: string | null;
}

export interface ActiveCall {
  call: CallSession;
  funerals: LinkedFuneral[];
  lead: LinkedLead | null;
}

const MAX_CALL_AGE_MS = 4 * 60 * 60 * 1000; // 4 hours

const isActive = (cs: CallSession) => {
  if (cs.ended_at || (cs.status !== 'initial' && cs.status !== 'answered')) return false;
  if (!cs.started_at) return false;
  if (!getCustomerPhone(cs) || getCustomerPhone(cs) === 'anonymous') return false;
  return Date.now() - new Date(cs.started_at).getTime() < MAX_CALL_AGE_MS;
};

const getCustomerPhone = (cs: CallSession) =>
  cs.direction === 'inbound' ? cs.from_number : cs.to_number;

export function useActiveCalls(): ActiveCall[] {
  const [activeCalls, setActiveCalls] = useState<ActiveCall[]>([]);
  const funeralCache = useRef<Map<string, LinkedFuneral[]>>(new Map());

  const resolveFunerals = useCallback(async (phone: string | null): Promise<LinkedFuneral[]> => {
    if (!phone) return [];
    const cached = funeralCache.current.get(phone);
    if (cached) return cached;

    const { data, error } = await supabase.rpc('get_funerals_by_phone', { p_phone: phone });
    const funerals = error ? [] : (data as LinkedFuneral[]) ?? [];
    funeralCache.current.set(phone, funerals);
    return funerals;
  }, []);

  const resolveLead = useCallback(async (cs: CallSession): Promise<LinkedLead | null> => {
    // If the call session already has a lead_id, use it directly
    if (cs.lead_id) {
      const { data } = await supabase
        .from('leads')
        .select('id, intent, stage, client_id')
        .eq('id', cs.lead_id)
        .single();
      if (data) {
        const { data: contact } = await supabase
          .from('clients')
          .select('name')
          .eq('id', data.client_id)
          .single();
        return {
          lead_id: data.id,
          contact_name: contact?.name || null,
          intent: data.intent,
          stage: data.stage,
        };
      }
    }
    // Fallback: find lead by phone
    const phone = getCustomerPhone(cs);
    if (!phone) return null;
    // Look the caller up by their NUMBER through the phone-endpoint table, then
    // read the person from clients. Matching a client's primary phone directly
    // would miss anyone reaching us from a second number.
    const { data: endpoint } = await supabase
      .from('contact_phones')
      .select('clients!inner(id, name)')
      .eq('phone', phone)
      .single();
    const contact = (endpoint as any)?.clients;
    if (!contact) return null;
    const { data: lead } = await supabase
      .from('leads')
      .select('id, intent, stage')
      .eq('client_id', contact.id)
      .order('opened_at', { ascending: false })
      .limit(1)
      .single();
    if (!lead) return null;
    return {
      lead_id: lead.id,
      contact_name: contact.name,
      intent: lead.intent,
      stage: lead.stage,
    };
  }, []);

  const addOrUpdateCall = useCallback(
    async (cs: CallSession) => {
      const [funerals, lead] = await Promise.all([
        resolveFunerals(getCustomerPhone(cs)),
        resolveLead(cs),
      ]);
      setActiveCalls((prev) => {
        const idx = prev.findIndex((ac) => ac.call.id === cs.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { call: cs, funerals, lead };
          return next;
        }
        return [...prev, { call: cs, funerals, lead }];
      });
    },
    [resolveFunerals, resolveLead],
  );

  const removeCall = useCallback((id: string) => {
    setActiveCalls((prev) => prev.filter((ac) => ac.call.id !== id));
  }, []);

  // Initial fetch
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const cutoff = new Date(Date.now() - MAX_CALL_AGE_MS).toISOString();
      const { data } = await supabase
        .from('call_sessions')
        .select('*')
        .is('ended_at', null)
        .in('status', ['initial', 'answered'])
        .gte('started_at', cutoff);

      if (cancelled || !data) return;

      const results: ActiveCall[] = await Promise.all(
        data.map(async (cs: CallSession) => {
          const [funerals, lead] = await Promise.all([
            resolveFunerals(getCustomerPhone(cs)),
            resolveLead(cs),
          ]);
          return { call: cs, funerals, lead };
        }),
      );

      setActiveCalls(results);
    })();

    return () => {
      cancelled = true;
    };
  }, [resolveFunerals, resolveLead]);

  // Realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel('active-calls')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'call_sessions' },
        (payload) => {
          const cs = payload.new as CallSession;

          if (payload.eventType === 'DELETE') {
            const old = payload.old as { id: string };
            removeCall(old.id);
            return;
          }

          if (isActive(cs)) {
            void addOrUpdateCall(cs);
          } else {
            removeCall(cs.id);
          }
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [addOrUpdateCall, removeCall]);

  return activeCalls;
}
